// src/redux/slices/pendingUserSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import { toast } from "react-toastify";

// Prefer backend error/message; fallback to first validation error or default.
function apiErrorMessage(err, defaultMsg) {
  const d = err.response?.data;
  if (d?.error) return d.error;
  if (d?.message) return d.message;
  if (d?.errors && typeof d.errors === "object") {
    const first = Object.values(d.errors).flat()[0];
    if (first) return first;
  }
  return defaultMsg;
}

//  Fetch all pending users
export const fetchPendingUsers = createAsyncThunk(
  "pendingUsers/fetchPendingUsers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/users/pending");
      return res.data.users;
    } catch (err) {
      const msg = apiErrorMessage(err, "Failed to fetch pending users");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  },
);

//  Approve role request by request ID (company_role_request id)
export const approveUser = createAsyncThunk(
  "pendingUsers/approveUser",
  async ({ requestId }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/role-approval/${requestId}`, {
        action: "approve",
      });
      toast.success(res.data.message || "User approved successfully!");
      return {
        requestId,
        progress: res.data.progress,
        status: res.data.status,
        approval_progress_by_role: res.data.approval_progress_by_role,
        step_status: res.data.step_status,
        current_step_role: res.data.current_step_role,
        next_step_role: res.data.next_step_info
          ? `${res.data.next_step_info.role_name} (${res.data.next_step_info.company_name})`
          : res.data.next_step_role,
      };
    } catch (err) {
      const msg = apiErrorMessage(err, "Failed to approve user");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  },
);

//  Reject role request by request ID (company_role_request id)
export const rejectUser = createAsyncThunk(
  "pendingUsers/rejectUser",
  async (requestId, { rejectWithValue }) => {
    try {
      await api.post(`/api/role-approval/${requestId}`, {
        action: "reject",
      });
      toast.error("User rejected from approval!");
      return requestId;
    } catch (err) {
      const msg = apiErrorMessage(err, "Failed to reject request");
      toast.error(msg);
      return rejectWithValue(msg);
    }
  },
);

const pendingUserSlice = createSlice({
  name: "pendingUsers",
  initialState: {
    users: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchPendingUsers
      .addCase(fetchPendingUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchPendingUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // approveUser
      .addCase(approveUser.fulfilled, (state, action) => {
        const {
          requestId,
          progress,
          status,
          approval_progress_by_role,
          step_status,
          current_step_role,
          next_step_role,
        } = action.payload;

        state.users = state.users.map((u) =>
          u.id === requestId
            ? {
                ...u,
                approval_progress: progress,
                approval_status: status,
                approval_progress_by_role,
                step_status,
                current_step_role,
                next_step_role,
                has_current_user_approved: true,
                can_current_user_act: false,
              }
            : u,
        );
      })

      // rejectUser
      .addCase(rejectUser.fulfilled, (state, action) => {
        state.users = state.users.filter((u) => u.id !== action.payload);
      });
  },
});

export default pendingUserSlice.reducer;
